import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { SUBTITLES_DEFAULT_SIZES } from '../../../components/subtitles/constants';
import { SubtitlesDevice, SubtitlesPositions, SubtitlesTextSize } from '../../../components/subtitles/types';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';
import { fetchTableConfigAction } from '../../apiActions/fetchTableConfigAction';

import type { SubtitlesMessage } from './types';
import { SubtitlesGroup, SubtitlesState } from './types';

const initialState: SubtitlesState = {
  group: SubtitlesGroup.GROUP1,
  isEnabled: true,
  isOnboarded: false,
  isAvailable: false,
  sizes: SUBTITLES_DEFAULT_SIZES,
  positionsRaw: {},
  subtitle: {
    text: '',
  },
  subtitleLanguages: [],
};

export const subtitlesSlice = createSlice({
  name: 'subtitles',
  initialState,
  reducers: {
    finishOnboarding: (state) => {
      state.isOnboarded = true;
    },
    setGroup: (state, action: PayloadAction<SubtitlesGroup>) => {
      state.group = action.payload;
    },
    setIsEnabled: (state, action: PayloadAction<boolean>) => {
      state.isEnabled = action.payload;
    },
    setSize: (state, action: PayloadAction<{ deviceType: SubtitlesDevice; size: SubtitlesTextSize }>) => {
      state.sizes[action.payload.deviceType] = action.payload.size;
    },
    setSubtitle: (state, action: PayloadAction<Partial<SubtitlesMessage>>) => {
      state.subtitle = {
        text: action.payload.content ?? '',
        isFinal: action.payload.isFinal,
      };
    },
    setPositions: (state, action: PayloadAction<{ gameType: string; positions: SubtitlesPositions }>) => {
      state.positionsRaw[action.payload.gameType] = action.payload.positions;
    },
    setIsAvailable: (state, action: PayloadAction<boolean>) => {
      state.isAvailable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      const playerConfigRaw = action.payload.rawResponse;

      const subsConfig = {
        subtitlesEnabled: playerConfigRaw.params.sub_enabled ?? state.group === SubtitlesGroup.GROUP2,
        onboarded: playerConfigRaw.params.sub_onboarded ?? false,
        size: playerConfigRaw.params.sub_sizes ?? '',
        positionsRaw: playerConfigRaw.params.sub_positions ?? '',
      };
      state.isEnabled = subsConfig.subtitlesEnabled;
      state.isOnboarded = subsConfig.onboarded;

      if (subsConfig.size) {
        const size = subsConfig.size;
        try {
          state.sizes = JSON.parse(size);
        } catch (e) {
          //
        }
      }

      if (subsConfig.positionsRaw) {
        const positionsRaw = subsConfig.positionsRaw;
        try {
          state.positionsRaw = JSON.parse(positionsRaw);
        } catch (e) {
          //
        }
      }
    });
    builder.addCase(fetchTableConfigAction.fulfilled, (state, action) => {
      state.subtitleLanguages = action.payload.rawResponse.params.subtitle?.split(',') ?? [];
    });
  },
});

export const subtitlesActions = subtitlesSlice.actions;
